import { Blob, DirectUpload } from "@rails/activestorage";
import { nanoid } from "@reduxjs/toolkit";
import { pull } from "lodash";
import { makeAutoObservable, runInAction } from "mobx";

import { FormElementAttributes, FormElementKind } from "@/gql/graphql";

import Section from "./Section";

export default class FormElement<
  Arguments extends Record<string, any> = Record<string, any>
> {
  id?: string;
  kind: FormElementKind;
  arguments: Partial<Arguments> = {};
  required = false;
  singleFile?: File;
  singleFileUrl?: string;
  singleFileSignedId?: string;
  multipleFiles: File[] = [];
  multipleFileUrls: string[] = [];
  multipleFileSignedIds: string[] = [];

  section: Section;

  /**
   * For react `key` prop only.
   */
  key = nanoid();

  constructor(section: Section, kind: FormElementKind) {
    makeAutoObservable(this);
    this.section = section;
    this.kind = kind;
  }

  setRequired(value: boolean): void {
    this.required = value;
  }

  setArguments(args: Arguments): void {
    this.arguments = args;
  }

  setArgument<Argument extends keyof Arguments>(
    argument: Argument,
    value: Partial<Arguments>[Argument]
  ): void {
    this.arguments[argument] = value;
  }

  updateArgument<Argument extends keyof Arguments>(
    argument: Argument,
    updater: (
      value: Partial<Arguments>[Argument]
    ) => Partial<Arguments>[Argument]
  ): void {
    this.setArgument(argument, updater(this.arguments[argument]));
  }

  remove(): void {
    this.section.removeElement(this);
  }

  uploadSingleFile(file: File): Promise<Blob> {
    this.singleFile = file;
    this.singleFileUrl = URL.createObjectURL(file);

    const baseUrl = import.meta.env.VITE_API_URL;
    const url = `${baseUrl}/api/direct_uploads`;
    const upload = new DirectUpload(file, url);

    return new Promise((resolve, reject) => {
      upload.create((error, blob) => {
        if (error) {
          this.singleFile = undefined;
          this.singleFileUrl = undefined;
          return reject(error);
        }
        runInAction(() => {
          this.singleFileUrl = `${baseUrl}/rails/active_storage/blobs/redirect/${blob.signed_id}/${blob.filename}`;
          this.singleFileSignedId = blob.signed_id;
        });
        resolve(blob);
      });
    });
  }

  uploadMultipleFile(file: File): Promise<Blob> {
    // TODO: handle setting signed ID race condition mismatch index
    this.multipleFiles.push(file);
    const fileUrl = URL.createObjectURL(file);
    this.multipleFileUrls.push(fileUrl);

    const baseUrl = import.meta.env.VITE_API_URL;
    const url = `${baseUrl}/api/direct_uploads`;
    const upload = new DirectUpload(file, url);

    return new Promise((resolve, reject) => {
      upload.create((error, blob) => {
        if (error) {
          pull(this.multipleFiles, file);
          pull(this.multipleFileUrls, fileUrl);
          return reject(error);
        }
        runInAction(() => {
          const index = this.multipleFileUrls.indexOf(fileUrl);
          this.multipleFileUrls[
            index
          ] = `${baseUrl}/rails/active_storage/blobs/redirect/${blob.signed_id}/${blob.filename}`;
          this.multipleFileSignedIds.push(blob.signed_id);
        });
        resolve(blob);
      });
    });
  }

  toAttributes(): FormElementAttributes {
    return {
      id: this.id,
      kind: this.kind,
      arguments: this.arguments,
      required: this.required,
      singleFile: this.singleFileSignedId,
      multipleFiles: this.multipleFileSignedIds,
    };
  }
}
